import React from 'react';
import { Button, ButtonProps } from '@mui/material';
import { usePermissions } from '../../hooks/usePermissions';
import { PermissionsEnum } from '../../utils/auth.types';

interface PermissionButtonProps extends Omit<ButtonProps, 'disabled'> {
  permission?: PermissionsEnum | string;
  permissions?: (PermissionsEnum | string)[];
  requireAll?: boolean;
  roles?: string[];
  hideIfNoPermission?: boolean;
  disableIfNoPermission?: boolean;
  children: React.ReactNode;
}

/**
 * Button component that respects user permissions
 */
export const PermissionButton: React.FC<PermissionButtonProps> = ({
  permission,
  permissions = [],
  requireAll = false,
  roles = [],
  hideIfNoPermission = false,
  disableIfNoPermission = true,
  children,
  ...buttonProps
}) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions, getUserRole, isSuperAdmin } = usePermissions();

  // Super admin bypasses all checks
  if (isSuperAdmin()) {
    return <Button {...buttonProps}>{children}</Button>;
  }

  // Check role-based access
  if (roles.length > 0) {
    const userRole = getUserRole();
    if (!roles.includes(userRole)) {
      if (hideIfNoPermission) return null;
      if (disableIfNoPermission) {
        return <Button {...buttonProps} disabled>{children}</Button>;
      }
    }
  }

  let hasAccess = true;

  // Check single permission
  if (permission) {
    hasAccess = hasPermission(permission);
  }

  // Check multiple permissions
  if (permissions.length > 0) {
    hasAccess = requireAll 
      ? hasAllPermissions(permissions)
      : hasAnyPermission(permissions);
  }

  // Handle no access
  if (!hasAccess) {
    if (hideIfNoPermission) {
      return null;
    }
    if (disableIfNoPermission) {
      return <Button {...buttonProps} disabled>{children}</Button>;
    }
  }

  return <Button {...buttonProps}>{children}</Button>;
};